/**
 * ProtectedRoute — guarda de rota por perfil (RN-M2-001, RN-GLOBAL-006, PC-04).
 *
 * - Sessão em resolução → estado de carregamento.
 * - Anônimo → redireciona ao `/login` (RN-M1-002); guarda a rota pretendida
 *   em `state.from` para retornar após o login.
 * - Autenticado sem o perfil exigido → aviso (ocultação é defesa em
 *   profundidade; o backend valida o grupo em toda rota — RN-GLOBAL-006).
 * - Toda rota exige MFA verificado NA sessão (`mfaVerified === true`, RN-M1-001,
 *   ADR-052 — antes só a área do admin). Sem isso, bloqueia com aviso: nenhum
 *   perfil alcança tela alguma sem ter passado pelo desafio TOTP.
 *
 * Segue o design (§ ProtectedRoute) na assinatura `allow={[...]}`.
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { hasAnyProfile, useAuth, UserProfile } from '../contexts/AuthContext';

export interface ProtectedRouteProps {
  /** Perfis autorizados a alcançar a rota. */
  readonly allow: readonly UserProfile[];
  readonly children: ReactNode;
}

export function ProtectedRoute({ allow, children }: ProtectedRouteProps): JSX.Element {
  const auth = useAuth();
  const location = useLocation();

  if (auth.loading) {
    return <p className="muted">Verificando acesso…</p>;
  }
  if (!auth.user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!hasAnyProfile(auth, allow)) {
    return <p className="notice">Seu perfil não tem acesso a esta área.</p>;
  }
  // RN-M1-001 / ADR-052: todo perfil precisa de MFA verificado NA sessão. A
  // sessão sem `mfaVerified` (que o AuthProvider já rejeita) é barrada por
  // defesa em profundidade.
  if (!auth.user.mfaVerified) {
    return <p className="notice">Esta área exige verificação em duas etapas (MFA).</p>;
  }
  return <>{children}</>;
}
